"use client";

import { Button } from "@/components/rac/button";
import { cn } from "@/utils/cn";
import { ImageGalleryCategory, ImageGalleryImage, useImageGallery } from "..";

interface GalleryCategoriesProps {
	categories: ImageGalleryCategory[];
	className?: string;
	selectedCategory?: ImageGalleryCategory;
	onCategoryChange?: (category: ImageGalleryCategory) => void;
}

export function GalleryCategories({
	categories,
	className,
	selectedCategory,
	onCategoryChange,
}: GalleryCategoriesProps) {
	const { images, goToIndex } = useImageGallery();

	if (!categories || categories.length === 0) return null;

	// Filter categories that have images
	const availableCategories = categories.filter(category =>
		images.some((image: ImageGalleryImage) => image.space === category.code),
	);

	if (availableCategories.length === 0) return null;

	return (
		<div className={cn("absolute bottom-0 left-0 w-full z-30", className)}>
			<div className="flex gap-2 overflow-x-auto max-w-full px-3 py-2 bg-gradient-to-t from-black/90 to-black/0 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/20 hover:scrollbar-thumb-white/40">
				{availableCategories.map((category, index) => {
					const imageCount = images.filter((image: ImageGalleryImage) => image.space === category.code).length;
					const isSelected = selectedCategory?.code === category.code;

					return (
						<Button
							key={`${category.code}-${index}`}
							data-category={category.code}
							onClick={() => {
								// Find first image in this category
								const categoryIndex = images.findIndex((image: ImageGalleryImage) => image.space === category.code);
								if (categoryIndex !== -1) {
									goToIndex(categoryIndex);
									onCategoryChange?.(category);
								}
							}}
							variant="link"
							className={cn(
								"relative px-2 py-1 text-sm transition-all duration-200 focus:outline-none after:bg-white",
								isSelected
									? "text-white font-medium after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:shadow-sm after:opacity-100"
									: "text-white/80 hover:text-white",
							)}
						>
							<span>{category.name.includes(":") ? category.name.split(":")[1] : category.name}</span>
							<span
								className={cn(
									"ml-1 rounded-full px-1 text-xs font-bold",
									isSelected ? "bg-white/40 text-white" : "bg-white/30 text-white/90",
								)}
							>
								{imageCount}
							</span>
						</Button>
					);
				})}
			</div>
		</div>
	);
}
